// src/pages/UpgradePlan.tsx
import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// ✅ Misma fuente de verdad que usas en Dashboard
import {
  PLANS,
  type PlanKey,
  type ResolutionKey,
  RESOLUTION_LABELS,
  BRAND_LIMIT_UNLIMITED,
} from "../config/plans";

type CreditPackKey = "XS" | "S" | "M" | "L" | "XL";
type Mode = "creator" | "brand";

type PacksResponse = {
  success: boolean;
  packs?: Array<{
    packId: string; // ej: "XS"
    credits: number;
    priceUsd: number;
    name?: string | null;
    badge?: string | null;
    description?: string | null;
  }>;
  error?: string;
};

type CheckoutResponse = {
  success: boolean;
  url?: string;
  error?: string;
};

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function prettyPlan(plan: string) {
  const p = (plan || "").toLowerCase();
  if (p === "free") return "Free";
  if (p === "basic") return "Basic";
  if (p === "pro") return "Pro";
  if (p === "premium") return "Premium";
  return plan || "un plan superior";
}

function formatUSDMonthly(n: number) {
  return n === 0 ? "Gratis" : `$${n.toFixed(0)} USD/mes`;
}

function planAccent(plan: PlanKey) {
  switch (plan) {
    case "free":
      return "from-slate-700/25 to-slate-950/25 border-white/10";
    case "basic":
      return "from-indigo-600/25 to-slate-950/25 border-indigo-400/20";
    case "pro":
      return "from-purple-600/25 to-slate-950/25 border-purple-400/20";
    case "premium":
      return "from-cyan-600/25 to-slate-950/25 border-cyan-400/20";
    default:
      return "from-slate-700/25 to-slate-950/25 border-white/10";
  }
}

function resolutionList(rs: ResolutionKey[]) {
  return rs.map((r) => RESOLUTION_LABELS[r] ?? r).join(" · ");
}

function isPlanKey(v: unknown): v is PlanKey {
  return v === "free" || v === "basic" || v === "pro" || v === "premium";
}

function planOrderValue(p: PlanKey) {
  switch (p) {
    case "free":
      return 0;
    case "basic":
      return 1;
    case "pro":
      return 2;
    case "premium":
      return 3;
    default:
      return 0;
  }
}

function toPackKey(v: string): CreditPackKey | null {
  const up = String(v || "").toUpperCase();
  if (up === "XS" || up === "S" || up === "M" || up === "L" || up === "XL") return up;
  return null;
}

// ✅ Lee modo guardado
function getSavedMode(): Mode {
  const saved = (localStorage.getItem("appcreativa_mode") || "").toLowerCase();
  return saved === "brand" ? "brand" : "creator";
}

/**
 * ✅ labels humanos para Brand Mode piezas
 */
function prettyBrandPiece(piece: string) {
  const p = (piece || "").toLowerCase();
  const map: Record<string, string> = {
    post_campaign: "Post para campaña",
    banner: "Banner",
    ads: "Anuncio (Ads)",
    product_creative: "Creativo de producto",
  };
  return map[p] ?? (piece || "esta pieza");
}

/**
 * ✅ prettyFeature con contexto (feature + piece)
 */
function prettyFeature(feature: string, piece?: string) {
  const f = (feature || "").toLowerCase();

  // Brand piece gating
  if (f === "brand_piece") {
    return `Brand Studio · ${prettyBrandPiece(piece || "")}`;
  }

  const map: Record<string, string> = {
    // Brand mode
    brand_persist: "Guardar Brand Kit (consistencia real)",
    brand_multiple: "Crear múltiples Brands",
    brand_advanced_fields: "Campos avanzados de Brand",
    brand_use_in_generation: "Aplicar Brand en generación",
    brand_kit: "Brand Kit (guardar y reutilizar)",

    // Creator mode
    "resolution:720p": "Calidad 720p",
    "resolution:1080p": "Calidad 1080p",
    "resolution:2k": "Calidad 2K",
    "resolution:4k": "Calidad 4K",
    "resolution:8k": "Calidad 8K",
    "buy-credits": "Comprar créditos extra",
    packs: "Packs de créditos",
    credits: "Créditos",
    "pro-feature": "Función Pro",
  };

  return map[f] ?? (feature ? feature : "esta función");
}

/**
 * ✅ Detecta si el upgrade viene por Brand (no solo brand_piece)
 */
function isBrandFeature(feature: string) {
  const f = (feature || "").toLowerCase();
  return f === "brand_piece" || f.startsWith("brand_");
}

/**
 * ✅ Recomendado inteligente para Brand:
 * - brand_persist / brand_kit / brand_use_in_generation -> Basic
 * - brand_multiple / brand_advanced_fields -> Pro
 * - brand_piece: ads/product_creative -> Pro ; banner/post_campaign -> Basic
 */
function recommendedPlanForBrand(feature: string, piece: string): PlanKey {
  const f = (feature || "").toLowerCase();
  const p = (piece || "").toLowerCase();

  if (f === "brand_piece") {
    if (p === "ads" || p === "product_creative") return "pro";
    return "basic";
  }

  if (f === "brand_multiple" || f === "brand_advanced_fields") return "pro";
  if (f === "brand_persist" || f === "brand_kit" || f === "brand_use_in_generation") return "basic";

  return "pro";
}

function brandLimitLabel(n: number) {
  return Number.isFinite(n) && n >= BRAND_LIMIT_UNLIMITED ? "Ilimitadas" : String(n);
}

function yesNo(v: boolean) {
  return v ? "✅" : "❌";
}

/**
 * ✅ Benefits por plan (Creator + Brand)
 * Brand Mode: alineado EXACTO a lo que pediste.
 */
function planBenefits(plan: PlanKey) {
  const cfg = PLANS[plan];
  const b = cfg.brand;

  // Creator mode bullets (se quedan simples y “vendibles”)
  const creatorBullets =
    plan === "free"
      ? ["720p para probar el flujo", "Créditos limitados para testear", "Ideal para empezar sin pagar"]
      : plan === "basic"
      ? ["720p + 1080p para publicar en redes", "Mejor ritmo de generación (más créditos)", "Ideal si generas seguido"]
      : plan === "pro"
      ? ["2K + 4K para clientes y entregables", "Más créditos para producción real", "Mejor opción para freelancers/agencias"]
      : ["Hasta 8K para calidad máxima", "Más créditos + escalabilidad", "Para campañas premium y máxima nitidez"];

  // Brand mode bullets: EXACTOS por plan (claros, simples)
  const brandBullets =
    plan === "free"
      ? [
          "✅ Puede entrar al Brand Studio",
          "✅ 1 Brand",
          "✅ Solo Nombre + Descripción",
          "❌ NO guarda kit (modo demo)",
          "❌ NO Advanced",
        ]
      : plan === "basic"
      ? [
          "✅ 1 Brand",
          "✅ Guarda kit base (consistencia real)",
          "✅ Aplica Brand al generar",
          "❌ NO multi-brands",
          "❌ NO Advanced",
        ]
      : plan === "pro"
      ? ["✅ Hasta 3 Brands", "✅ Guarda kit + ✅ Aplica Brand", "✅ Advanced (mood / referencia)", "✅ Piezas pro listas para negocio"]
      : ["✅ Brands ilimitadas", "✅ Todo desbloqueado", "✅ Advanced", "✅ Ideal para agencias / múltiples clientes"];

  // Piezas disponibles (claridad)
  const pieces = (b.allowedPieces || []).map((x) => `• ${prettyBrandPiece(String(x))}`);

  // “Facts” compactos (chips) para que la gente compare rápido
  const brandFacts = [
    `Brands: ${brandLimitLabel(b.brandKitsLimit)}`,
    `${yesNo(b.canPersistBrandKit)} Guarda kit`,
    `${yesNo(b.canUseBrandInGeneration)} Aplica Brand`,
    `${yesNo(b.canUseAdvancedBrandFields)} Advanced`,
  ];

  return { creatorBullets, brandBullets, pieces, brandFacts };
}

export default function UpgradePlan() {
  const query = useQuery();
  const navigate = useNavigate();
  const { user, getAccessToken } = useAuth();

  const feature = query.get("feature") || "";
  const piece = query.get("piece") || "";
  const requiredRaw = query.get("required") || "";
  const requiredPlan: PlanKey | null = isPlanKey(requiredRaw) ? (requiredRaw as PlanKey) : null;

  const success = query.get("success") === "1";
  const canceled = query.get("canceled") === "1";

  const isBrandUpgrade = isBrandFeature(feature);

  // ✅ decidir “volver” según modo
  const backRoute = useMemo(() => {
    const mode = getSavedMode();
    return mode === "brand" ? "/brand" : "/dashboard";
  }, []);

  // ✅ ocultar packs CTA si viene por Brand (para no distraer)
  const showPacksCTA = !isBrandUpgrade;

  // ✅ UX: si viene desde pack/credits, precargamos packs (pero NO en brand)
  const shouldSuggestPacks = useMemo(() => {
    if (isBrandUpgrade) return false;
    const f = feature.toLowerCase();
    return f.includes("packs") || f.includes("buy-credits") || f.includes("credits");
  }, [feature, isBrandUpgrade]);

  // ✅ Auto-scroll a planes cuando viene required/feature (si no es success/canceled)
  useEffect(() => {
    if (success || canceled) return;
    if (!requiredPlan && !feature) return;

    const t = window.setTimeout(() => {
      const el = document.getElementById("plans");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);

    return () => window.clearTimeout(t);
  }, [requiredPlan, feature, success, canceled]);

  // Packs
  const [packs, setPacks] = useState<
    Array<{
      packId: string;
      credits: number;
      priceUsd: number;
      name?: string | null;
      badge?: string | null;
      description?: string | null;
    }>
  >([]);
  const [packsLoading, setPacksLoading] = useState(false);
  const [packsError, setPacksError] = useState("");

  // Stripe checkout states
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState<PlanKey | null>(null);
  const [checkoutLoadingPack, setCheckoutLoadingPack] = useState<CreditPackKey | null>(null);
  const [checkoutError, setCheckoutError] = useState<string>("");

  const loadPacks = useCallback(async () => {
    setPacksError("");
    setPacksLoading(true);
    try {
      const resp = await fetch("/api/packs", { method: "GET" });
      const data = (await resp.json()) as PacksResponse;

      if (!resp.ok || !data?.success || !Array.isArray(data.packs)) {
        throw new Error(data?.error || "No se pudieron cargar los packs.");
      }

      const sorted = [...data.packs].sort((a, b) => a.priceUsd - b.priceUsd);
      setPacks(sorted);
    } catch (e) {
      setPacks([]);
      setPacksError(e instanceof Error ? e.message : "Error cargando packs.");
    } finally {
      setPacksLoading(false);
    }
  }, []);

  useEffect(() => {
    if (shouldSuggestPacks) loadPacks();
  }, [shouldSuggestPacks, loadPacks]);

  const title = "Upgrade";

  const subtitle = useMemo(() => {
    const feat = prettyFeature(feature, piece);
    const req = requiredPlan ? prettyPlan(requiredPlan) : "";

    if (success) return "✅ Pago completado. Tu plan/créditos se aplicarán en segundos.";
    if (canceled) return "Pago cancelado. Puedes intentarlo de nuevo cuando quieras.";

    if (feature && requiredPlan) return `Para usar “${feat}” necesitas el plan ${req} (o superior).`;
    if (feature) return `Para usar “${feat}” necesitas un plan superior.`;

    return isBrandUpgrade
      ? "Desbloquea consistencia de marca, más brands y piezas listas para negocio."
      : "Desbloquea más resoluciones, más créditos y mejor calidad de generación.";
  }, [feature, piece, requiredPlan, success, canceled, isBrandUpgrade]);

  // ✅ RECOMENDADO INTELIGENTE (respeta required, si no: Brand->según feature/piece)
  const recommendedPlan: PlanKey = useMemo(() => {
    if (requiredPlan) return requiredPlan;
    if (isBrandUpgrade) return recommendedPlanForBrand(feature, piece);
    return "premium";
  }, [requiredPlan, isBrandUpgrade, feature, piece]);

  const startCheckoutPlan = async (plan: PlanKey) => {
    setCheckoutError("");

    if (!user) {
      navigate("/login");
      return;
    }

    if (plan === "free") {
      setCheckoutError("El plan Free no requiere pago. Elige Basic/Pro/Premium.");
      return;
    }

    try {
      setCheckoutLoadingPlan(plan);

      const token = await getAccessToken();
      if (!token) throw new Error("Tu sesión no está activa. Inicia sesión de nuevo.");

      const resp = await fetch("/api/stripe/checkout-plan", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ planId: plan }),
      });

      const data = (await resp.json()) as CheckoutResponse;

      if (!resp.ok || !data?.success || !data.url) {
        throw new Error(data?.error || "No se pudo iniciar el checkout del plan.");
      }

      window.location.href = data.url;
    } catch (e) {
      setCheckoutError(e instanceof Error ? e.message : "Error iniciando checkout del plan.");
    } finally {
      setCheckoutLoadingPlan(null);
    }
  };

  const startCheckoutPack = async (packKey: CreditPackKey) => {
    setCheckoutError("");

    if (!user) {
      navigate("/login");
      return;
    }

    try {
      setCheckoutLoadingPack(packKey);

      const token = await getAccessToken();
      if (!token) throw new Error("Tu sesión no está activa. Inicia sesión de nuevo.");

      const resp = await fetch("/api/stripe/checkout-pack", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ packKey }),
      });

      const data = (await resp.json()) as CheckoutResponse;

      if (resp.status === 403) {
        navigate("/upgrade?feature=packs&required=basic");
        return;
      }

      if (!resp.ok || !data?.success || !data.url) {
        throw new Error(data?.error || "No se pudo iniciar el checkout del pack.");
      }

      window.location.href = data.url;
    } catch (e) {
      setCheckoutError(e instanceof Error ? e.message : "Error iniciando checkout del pack.");
    } finally {
      setCheckoutLoadingPack(null);
    }
  };

  const tipText = useMemo(() => {
    if (isBrandUpgrade) {
      return "Tip: Si haces campañas completas, Pro te da multi-brands + advanced y desbloquea Ads + creativos de producto.";
    }
    return "Tip: Si tu objetivo es calidad alta (2K/4K/8K), elige un plan que lo permita y luego complementa con packs cuando lo necesites.";
  }, [isBrandUpgrade]);

  return (
    <div className="min-h-screen text-white bg-[#05050a]">
      <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-black to-slate-950">
        {/* Header */}
        <header className="px-6 md:px-10 py-6 border-b border-white/10 bg-[#070711]/70 backdrop-blur">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-cyan-400 flex items-center justify-center text-xl font-bold shadow-lg shadow-indigo-500/30">
                O
              </div>
              <div>
                <h1 className="text-lg md:text-xl font-semibold tracking-tight">Ovylon · AI Studio</h1>
                <p className="text-[11px] text-gray-400 uppercase tracking-[0.2em]">Planes y upgrades</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(backRoute)}
                className="text-[11px] md:text-xs px-3 py-2 rounded-xl border border-white/15 hover:bg-white/10 transition"
              >
                ← Volver
              </button>

              {!user && (
                <button
                  onClick={() => navigate("/login")}
                  className="text-[11px] md:text-xs px-3 py-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400 transition font-semibold"
                >
                  Iniciar sesión
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="px-6 md:px-10 py-10">
          <div className="max-w-6xl mx-auto">
            {/* Hero */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 md:p-10 backdrop-blur-md shadow-xl shadow-indigo-900/30">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold">{title}</h2>
                  <p className="mt-3 text-gray-300 text-sm md:text-base max-w-2xl">{subtitle}</p>

                  {(feature || requiredPlan) && !success && !canceled && (
                    <div className="mt-4 text-[11px] text-gray-400 space-y-1">
                      {feature && (
                        <p>
                          <span className="text-gray-500">Feature:</span>{" "}
                          <span className="text-gray-200">{feature}</span>
                        </p>
                      )}
                      {piece && isBrandUpgrade && (
                        <p>
                          <span className="text-gray-500">Piece:</span>{" "}
                          <span className="text-gray-200">{piece}</span>
                        </p>
                      )}
                      {requiredPlan && (
                        <p>
                          <span className="text-gray-500">Required:</span>{" "}
                          <span className="text-gray-200">{requiredPlan}</span>
                        </p>
                      )}
                    </div>
                  )}

                  {checkoutError && <p className="mt-4 text-sm text-red-300">{checkoutError}</p>}

                  {success && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      <button
                        onClick={() => navigate(backRoute)}
                        className="px-4 py-2.5 rounded-2xl text-sm font-semibold
                          bg-gradient-to-r from-indigo-500 to-purple-500
                          hover:from-indigo-400 hover:to-purple-400
                          transition shadow-lg shadow-indigo-500/30"
                      >
                        Volver
                      </button>
                      <button
                        onClick={() => navigate("/upgrade")}
                        className="px-4 py-2.5 rounded-2xl text-sm font-semibold border border-white/15 hover:bg-white/10 transition"
                      >
                        Ver planes
                      </button>
                    </div>
                  )}

                  {!success && (
                    <div className="mt-6 flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          const el = document.getElementById("plans");
                          if (el) el.scrollIntoView({ behavior: "smooth" });
                        }}
                        className="px-4 py-2.5 rounded-2xl text-sm font-semibold
                          bg-gradient-to-r from-indigo-500 to-purple-500
                          hover:from-indigo-400 hover:to-purple-400
                          transition shadow-lg shadow-indigo-500/30"
                      >
                        Ver planes
                      </button>

                      <button
                        onClick={() => navigate(backRoute)}
                        className="px-4 py-2.5 rounded-2xl text-sm font-semibold border border-white/15 hover:bg-white/10 transition"
                      >
                        Volver
                      </button>

                      {/* ✅ ocultar packs en upgrades de Brand */}
                      {showPacksCTA && (
                        <button
                          onClick={() => {
                            const el = document.getElementById("packs");
                            if (el) el.scrollIntoView({ behavior: "smooth" });
                            else loadPacks();
                          }}
                          className="px-4 py-2.5 rounded-2xl text-sm font-semibold border border-white/15 hover:bg-white/10 transition"
                        >
                          Ver packs
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-indigo-400/20 bg-gradient-to-br from-indigo-500/15 to-purple-500/10 p-5">
                  <p className="text-xs text-indigo-200 font-semibold mb-1">Consejo rápido</p>
                  <p className="text-[12px] text-gray-300">{tipText}</p>
                  <p className="mt-3 text-[11px] text-gray-400">
                    (Al pagar, Stripe te llevará a Checkout y volverás aquí con success/canceled.)
                  </p>
                </div>
              </div>
            </div>

            {/* Plans grid */}
            <div id="plans" className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {(["free", "basic", "pro", "premium"] as PlanKey[]).map((k) => {
                const p = PLANS[k];

                const isRecommended = k === recommendedPlan;
                const meetsRequirement = requiredPlan ? planOrderValue(k) >= planOrderValue(requiredPlan) : false;

                const someLoading = checkoutLoadingPlan !== null || checkoutLoadingPack !== null;

                const benefits = planBenefits(k);

                return (
                  <div
                    key={k}
                    className={`relative rounded-3xl border bg-gradient-to-br ${planAccent(k)} p-5 backdrop-blur-md hover:bg-white/5 transition`}
                  >
                    {(isRecommended || meetsRequirement) && (
                      <span
                        className={`absolute top-3 right-3 text-[10px] px-2 py-1 rounded-full border
                          ${
                            isRecommended
                              ? "bg-indigo-500/20 border-indigo-400/30 text-indigo-200"
                              : "bg-white/10 border-white/15 text-gray-200"
                          }`}
                      >
                        {isRecommended ? "Recomendado" : "Compatible"}
                      </span>
                    )}

                    <div className="flex items-center justify-between">
                      <p className="text-lg font-bold text-white">{p.name}</p>
                      <p className="text-[11px] text-gray-300">{formatUSDMonthly(p.priceMonthlyUSD)}</p>
                    </div>

                    <div className="mt-3">
                      <p className="text-[11px] text-gray-400">Créditos / mes</p>
                      <p className="text-2xl font-bold text-white">{p.creditsMonthly}</p>
                    </div>

                    <div className="mt-4">
                      <p className="text-[11px] text-gray-400 mb-1">Resoluciones permitidas</p>
                      <p className="text-[12px] text-gray-200">{resolutionList(p.allowedResolutions)}</p>
                    </div>

                    {/* ✅ BENEFICIOS: Creator + Brand */}
                    <div className="mt-5 space-y-3">
                      <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                        <div className="flex items-center justify-between">
                          <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400">Creator Mode</p>
                          <span className="text-[10px] px-2 py-1 rounded-full border border-white/10 bg-white/5 text-gray-200">
                            {k === "free" ? "Starter" : k === "basic" ? "Creator" : k === "pro" ? "Pro Creator" : "Max Quality"}
                          </span>
                        </div>

                        <ul className="mt-3 space-y-1 text-[12px] text-gray-300">
                          {benefits.creatorBullets.map((x) => (
                            <li key={x}>• {x}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="rounded-2xl border border-indigo-400/15 bg-indigo-500/10 p-4">
                        <div className="flex items-center justify-between">
                          <p className="text-[11px] uppercase tracking-[0.2em] text-indigo-200">Brand Mode</p>
                          <span className="text-[10px] px-2 py-1 rounded-full border border-indigo-400/20 bg-indigo-500/10 text-indigo-100">
                            {k === "free" ? "Demo" : k === "basic" ? "Consistencia" : k === "pro" ? "Freelancer" : "Agencia"}
                          </span>
                        </div>

                        {/* chips comparativos rápidos */}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {benefits.brandFacts.map((x) => (
                            <span
                              key={x}
                              className="text-[10px] px-2 py-1 rounded-full border border-indigo-400/15 bg-black/20 text-gray-200"
                            >
                              {x}
                            </span>
                          ))}
                        </div>

                        <ul className="mt-3 space-y-1 text-[12px] text-gray-200">
                          {benefits.brandBullets.map((x) => (
                            <li key={x}>• {x}</li>
                          ))}
                        </ul>

                        <div className="mt-3 pt-3 border-t border-white/10">
                          <p className="text-[11px] text-gray-300">
                            <span className="text-gray-400">Piezas:</span>
                          </p>
                          <div className="mt-2 text-[12px] text-gray-300 space-y-1">
                            {benefits.pieces.slice(0, 4).map((x) => (
                              <div key={x}>{x}</div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {requiredPlan && (
                        <div className="flex items-center justify-between text-[12px] text-gray-300">
                          <span className="text-gray-400">Para tu feature</span>
                          <span className="text-gray-200 font-medium">{meetsRequirement ? "✅ Sí" : "❌ No"}</span>
                        </div>
                      )}
                    </div>

                    <button
                      disabled={k === "free" || someLoading}
                      onClick={() => startCheckoutPlan(k)}
                      className="mt-5 w-full px-4 py-2.5 rounded-2xl text-sm font-semibold
                        bg-gradient-to-r from-indigo-500 to-purple-500
                        hover:from-indigo-400 hover:to-purple-400
                        transition shadow-lg shadow-indigo-500/30
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {checkoutLoadingPlan === k ? "Redirigiendo a pago..." : `Elegir ${p.name}`}
                    </button>

                    <button
                      onClick={() => navigate(backRoute)}
                      className="mt-2 w-full px-4 py-2 rounded-2xl text-[12px]
                        border border-white/15 hover:bg-white/10 transition"
                    >
                      Volver
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Packs section (se mantiene, pero el CTA se oculta para Brand upgrades) */}
            <div
              id="packs"
              className="mt-8 bg-white/5 border border-white/10 rounded-3xl p-6 md:p-8 backdrop-blur-md shadow-xl shadow-indigo-900/20"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-white">Packs de créditos (económico)</h3>
                  <p className="mt-2 text-sm text-gray-300 max-w-2xl">
                    Puedes complementar tu plan con packs cuando necesites más créditos.
                    <span className="text-gray-400">
                      {" "}
                      (Si estás en Free, primero debes subir mínimo a Basic para habilitar packs.)
                    </span>
                  </p>
                </div>

                <button
                  onClick={loadPacks}
                  disabled={packsLoading || isBrandUpgrade}
                  className="text-xs px-3 py-2 rounded-xl border border-white/15 hover:bg-white/10 transition disabled:opacity-50"
                  title={isBrandUpgrade ? "Para Brand Studio, enfócate en elegir plan." : ""}
                >
                  {packsLoading ? "Cargando..." : "🔄 Cargar / Recargar packs"}
                </button>
              </div>

              {packsError && <p className="mt-3 text-sm text-red-300">{packsError}</p>}

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                {packsLoading && packs.length === 0 && (
                  <div className="col-span-full text-sm text-gray-400">Cargando packs...</div>
                )}

                {!packsLoading && packs.length === 0 && !packsError && (
                  <div className="col-span-full text-sm text-gray-400">
                    Aún no hay packs disponibles. (Asegúrate de tener /api/packs funcionando)
                  </div>
                )}

                {packs.map((p) => {
                  const packKey = toPackKey(p.packId);
                  const disabled =
                    isBrandUpgrade || !packKey || checkoutLoadingPlan !== null || checkoutLoadingPack !== null;

                  return (
                    <div
                      key={p.packId}
                      className="relative rounded-2xl border border-white/10 bg-black/30 p-4 hover:bg-white/5 transition"
                    >
                      {p.badge && (
                        <span className="absolute top-2 right-2 text-[10px] px-2 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-200">
                          {p.badge}
                        </span>
                      )}

                      <p className="text-sm font-semibold text-white">{p.name || p.packId}</p>
                      <p className="text-xs text-gray-400 mt-1">+{p.credits} créditos</p>
                      <p className="text-xs text-gray-300 mt-2">USD ${p.priceUsd}</p>

                      {p.description && <p className="text-[11px] text-gray-500 mt-2">{p.description}</p>}

                      <button
                        disabled={disabled}
                        onClick={() => {
                          if (!packKey) return;
                          startCheckoutPack(packKey);
                        }}
                        className="mt-3 w-full px-3 py-2 rounded-xl text-[12px] font-semibold
                          border border-white/15 hover:bg-white/10 transition
                          disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {checkoutLoadingPack === packKey ? "Redirigiendo..." : "Comprar pack"}
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                <button
                  onClick={() => navigate(backRoute)}
                  className="px-4 py-2 rounded-2xl text-sm font-semibold border border-white/15 hover:bg-white/10 transition"
                >
                  Volver
                </button>
              </div>
            </div>

            {/* Footer note */}
            <div className="mt-8 text-center text-[11px] text-gray-500">
              Tip: query params:{" "}
              <span className="text-gray-300 font-medium">
                /upgrade?feature=brand_piece&amp;piece=ads&amp;required=pro
              </span>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}



